import moment from 'moment';
import { ACCESS_ROLE, ROROTEL_ACCESS_TOKEN } from './globalConstants';
import { Toaster } from './toaster';

export const capitalizeFirstLetter = (str) => {
  // Check if the input is not a string or if it's an empty string
  if (typeof str !== 'string' || str.length === 0) {
    return str; // Return the original value
  }
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const quotationStatus = (status) => {
  if (status === 0) return 'Lost';
  if (status === 1) return 'Pending';
  if (status === 4) return 'Accepted';
  if (status === 3) return 'Rejected';
  if (status === 5) return 'Save as Draft';
  if (status === 6) return 'Allocated';
};

export const getAccessToken = () => localStorage.getItem(ROROTEL_ACCESS_TOKEN);
export const getAccessRole = () => localStorage.getItem(ACCESS_ROLE);

export const getObjectLength = (object) => {
  try {
    return Object.keys(object).length;
  } catch (_) {
    return 0;
  }
};

export const camelCaseToText = (str) => {
  if (typeof str !== 'string' || str.length === 0) {
    return str; // Return the original value if it's not a non-empty string
  }
  return str
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
};

export const stringToColour = (str = '') => {
  if (!str || str.length < 3) return '#000000';
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let j = 0; j < 3; j++) {
    const value = (hash >> (j * 8)) & 0xff;
    const hex = value.toString(16).padStart(2, '0');
    color += hex;
  }
  return color;
};

export const getStringInitials = (str = '') => {
  const value = str == null ? '' : String(str).trim();
  if (!value) return 'U';

  const acronym = value
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0].toUpperCase())
    .join('');

  return acronym || 'U';
};

export const darkenColor = (hexColor, factor = 20) => {
  if (!hexColor) return '#fff';
  hexColor = hexColor.replace('#', '');
  const r = parseInt(hexColor.slice(0, 2), 16);
  const g = parseInt(hexColor.slice(2, 4), 16);
  const b = parseInt(hexColor.slice(4, 6), 16);
  const newR = Math.max(0, r - factor);
  const newG = Math.max(0, g - factor);
  const newB = Math.max(0, b - factor);
  const darkenedColor = `#${newR.toString(16).padStart(2, '0')}${newG
    .toString(16)
    .padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  return darkenedColor;
};

// export const downloadDocFile = async (url) => {
//   let filename = 'doc';
//   let request = `${url}?token=${accessToken}`;
//   let a = document.createElement('a');
//   a.target = '_blank';
//   a.href = request;
//   a.download = filename;
//   a.style.display = 'none';
//   document.body.appendChild(a);
//   a.click();
//   document.body.removeChild(a);
// };

export const downloadFile = ({ fileResponse, fileName = '' }) => {
  if (fileResponse?.url) {
    fetch(fileResponse.url)
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a); // Append to body to be safe
        a.click();
        document.body.removeChild(a); // Clean up after click
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch((err) => {
        Toaster.error('Error occurred while downloading the file');
      });
  } else {
    Toaster.error('Some error occurred while downloading sample file');
  }
};

// Pull the filename the API chose out of `Content-Disposition`. Used where the
// server is the single source of truth for naming (invoice PDFs and their CDR
// sheets, which must share a base name to stay matchable).
const parseContentDispositionFileName = (response) => {
  const header = response?.headers?.get?.('Content-Disposition') || '';

  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      return encoded[1].trim();
    }
  }

  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain?.[1]?.trim() || '';
};

export const downloadFileFromURL = ({
  url = '',
  cb = () => {},
  fileName = '',
  token = '',
  method = 'GET',
  body = null,
  onResponse = () => {},
  useServerFileName = false,
}) => {
  if (!url) Toaster.error('Download failed: URL is required');

  if (!token) Toaster.error('Download failed: Authorization token is required');

  cb(true); // Start loading (e.g., show spinner)

  fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      // Only send a JSON content type when there is actually a body, so plain
      // GET downloads keep behaving exactly as before.
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  })
    .then(async (response) => {
      if (!response.ok) {
        // Failed downloads answer with the API's usual JSON error envelope.
        const message = await response
          .clone()
          .json()
          .then((data) => data?.[0]?.msg ?? data?.data?.[0]?.msg)
          .catch(() => null);

        throw new Error(message || `HTTP error! Status: ${response.status}`);
      }
      onResponse(response);

      const serverFileName = useServerFileName
        ? parseContentDispositionFileName(response)
        : '';

      return response.blob().then((blob) => ({ blob, serverFileName }));
    })
    .then(({ blob, serverFileName }) => {
      if (!blob) {
        throw new Error('No file data received');
      }

      const objectUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');

      anchor.href = objectUrl;
      anchor.download =
        serverFileName || fileName || `download_${new Date().getTime()}`; // Fallback filename
      anchor.style.display = 'none';

      document.body.appendChild(anchor);
      anchor.click();

      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
        document.body.removeChild(anchor);
      }, 100);
    })
    .catch((error) => {
      console.error('Download failed:', error);
      Toaster.error(error.message || 'Download failed. Please try again.');
    })
    .finally(() => {
      cb(false); 
    });
};

export const formatCurrency = (
  value,
  { currency = 'GBP', locale = 'en-GB', fallback = '—' } = {},
) => {
  if (value === null || value === undefined || value === '') return fallback;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return fallback;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
};

export const formatNumber = (
  value,
  { locale = 'en-GB', fallback = '—' } = {},
) => {
  if (value === null || value === undefined || value === '') return fallback;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return fallback;
  return new Intl.NumberFormat(locale).format(numeric);
};

export const snakeToTitleCase = (str) => {
  if (!str) return;
  return str
    .toLowerCase() // Convert the entire string to lowercase
    .split('_') // Split the string into an array of words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
    .join(' '); // Join the words back together with a space
};

let wait = false;
export const throttlingFunc = (cb, delay) => {
  return (...args) => {
    if (wait) {
      return;
    }
    cb(...args);
    wait = true;
    setTimeout(() => {
      wait = false;
    }, delay);
  };
};

export const handleDate = (value, customDate = {}) => {
  const todayDate = moment().format('YYYY-MM-DD');
  const yesterdayDate = moment().subtract(1, 'days').format('YYYY-MM-DD');
  switch (value) {
    case 'Today':
      return {
        from: todayDate,
        to: todayDate,
      };

    case 'Yesterday':
      return {
        from: yesterdayDate,
        to: yesterdayDate,
      };

    case 'Last 7 Days':
      return {
        from: moment().subtract(7, 'days').format('YYYY-MM-DD'),
        to: todayDate,
      };

    case 'Last 30 Days':
      return {
        from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
        to: todayDate,
      };

    case 'This Month':
      return {
        from: moment().startOf('month').format('YYYY-MM-DD'),
        to: moment().endOf('month').format('YYYY-MM-DD'),
      };

    case 'Last Month':
      return {
        from: moment()
          .subtract(1, 'month')
          .startOf('month')
          .format('YYYY-MM-DD'),
        to: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
      };
    case 'Last 3 Months':
      return {
        from: moment()
          .subtract(3, 'month')
          .startOf('month')
          .format('YYYY-MM-DD'),
        to: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
      };
    case 'Last 6 Months':
      return {
        from: moment()
          .subtract(6, 'month')
          .startOf('month')
          .format('YYYY-MM-DD'),
        to: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
      };
    case 'Last 12 Months':
      return {
        from: moment()
          .subtract(12, 'month')
          .startOf('month')
          .format('YYYY-MM-DD'),
        to: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
      };
    case 'Last 24 Months':
      return {
        from: moment()
          .subtract(24, 'month')
          .startOf('month')
          .format('YYYY-MM-DD'),
        to: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
      };
    case 'Custom':
      return {
        from: customDate?.from
          ? moment(customDate.from).format('YYYY-MM-DD')
          : '',
        to: customDate?.to ? moment(customDate.to).format('YYYY-MM-DD') : '',
      };

    default:
      return { from: '', to: '' };
  }
};
